/** The version file that the build writes next to the page; the server never keeps it in a cache. */
export interface VersionFile {
  build: string;
  sha: string;
  built_at: string;
  shell: string;
  break: number;
}

export const VERSION_PATH = "/version.json";
export const CHECK_MS = 10 * 60_000;
export const TICK_MS = 60_000;
/** A tick that comes this late means that the computer slept. */
export const WAKE_GAP_MS = 2 * TICK_MS;

/** The file when it is valid; Hosting answers an unknown path with the page, so HTML gives null. */
export function parseVersion(raw: unknown): VersionFile | null {
  if (!raw || typeof raw !== "object") return null;
  const file = raw as Record<string, unknown>;
  if (typeof file.build !== "string" || !file.build) return null;
  return {
    build: file.build,
    sha: typeof file.sha === "string" ? file.sha : "",
    built_at: typeof file.built_at === "string" ? file.built_at : "",
    shell: typeof file.shell === "string" ? file.shell : "",
    break: Number.isInteger(file.break) ? (file.break as number) : 0,
  };
}

/** Read the version file from the network; null when it is missing or the page is offline. */
export async function fetchVersion(get: typeof fetch = fetch): Promise<VersionFile | null> {
  try {
    const response = await get(`${VERSION_PATH}?t=${Date.now()}`, { cache: "no-store" });
    if (!response.ok) return null;
    return parseVersion(await response.json());
  } catch {
    return null;
  }
}

/** True when the file names a different build than the page; a rollback is a new build too. */
export const isNewBuild = (file: VersionFile | null, page: string): file is VersionFile => file !== null && file.build !== page;

/** True when a tick is so late that the computer slept between two ticks. */
export const woke = (lastTick: number, now: number): boolean => now - lastTick >= WAKE_GAP_MS;

/** The text "checked N min ago" of the last version check. */
export function checkedText(checkedAt: number | null, now: number): string {
  if (checkedAt === null) return "not checked yet";
  const minutes = Math.floor(Math.max(0, now - checkedAt) / 60_000);
  if (minutes < 1) return "checked just now";
  if (minutes < 120) return `checked ${minutes} min ago`;
  return `checked ${Math.floor(minutes / 60)} h ago`;
}
